import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, ShieldOff } from 'lucide-react';
import { SafeAvatarImage } from '../SafeAvatarImage';
import { hostCardStyle } from '../HostSettingsCard';
import { getListingPresentation } from '../../utils/hostListings';
import { formatEuropeanDate } from '../../utils/dateFormat';

const listMotionProps = {
  layout: true,
  initial: { opacity: 0, y: 16, scale: 0.985 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -10, scale: 0.985 },
  transition: { duration: 0.24, ease: [0.25, 0.1, 0.25, 1] },
};

const parseDay = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const guestInitial = (name) => (String(name || '').trim().charAt(0) || 'G').toUpperCase();

const buildStatusPill = ({ passActive, checkout, today }) => {
  const parsedCheckout = parseDay(checkout);
  if (passActive === false) return { label: 'Cancelled', bg: 'rgba(221,6,8,0.14)', color: '#DD0608' };
  if (!parsedCheckout) return { label: 'Active', bg: 'rgba(17,181,57,0.14)', color: '#169C3A' };
  if (today > parsedCheckout) return { label: 'Expired', bg: 'rgba(221,6,8,0.14)', color: '#DD0608' };
  if (today.getTime() === parsedCheckout.getTime()) return { label: 'Expiring today', bg: 'rgba(255,169,48,0.18)', color: '#B37200' };
  return { label: 'Active', bg: 'rgba(17,181,57,0.14)', color: '#169C3A' };
};

const buildProgress = ({ checkIn, checkout, today }) => {
  const start = parseDay(checkIn);
  const end = parseDay(checkout);
  if (!start || !end) return null;
  const totalDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
  const currentDay = Math.min(totalDays, Math.max(1, Math.round((today - start) / 86400000) + 1));
  const pct = Math.min(100, Math.max(0, (currentDay / totalDays) * 100));
  return { totalDays, currentDay, pct };
};

const AvatarFallback = ({ name, testId }) => (
  <div data-testid={testId} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#F1B7B0,#DD0608)', color: '#fff', fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700 }}>
    {guestInitial(name)}
  </div>
);

export const HostApprovedInviteCard = ({ invite, today, section, onMessage, onDeactivate }) => {
  const listingMeta = useMemo(
    () => getListingPresentation({ title: invite.listing_name, address: invite.listing_address }),
    [invite],
  );
  const pill = useMemo(
    () => buildStatusPill({ passActive: invite.guest_pass_active, checkout: invite.checkout_date, today }),
    [invite, today],
  );
  const progress = useMemo(
    () => buildProgress({ checkIn: invite.check_in_date, checkout: invite.checkout_date, today }),
    [invite, today],
  );
  const venueCount = Number(invite.venues_visited_count || 0);
  const canMessage = !!invite.conversation_id;

  return (
    <motion.div
      key={invite.invite_id}
      data-testid={`host-access-card-${invite.invite_id}`}
      {...listMotionProps}
      style={{ ...hostCardStyle, background: 'var(--surface)', borderRadius: 24, padding: '14px 16px 16px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 62, height: 62, borderRadius: 999, overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0, border: '1px solid var(--card-border)' }}>
          <SafeAvatarImage
            src={invite.guest_photo_url}
            alt={invite.guest_name || 'Guest'}
            dataTestId={`host-invite-guest-avatar-${invite.invite_id}`}
            fallback={<AvatarFallback name={invite.guest_name} testId={`host-invite-guest-avatar-fallback-${invite.invite_id}`} />}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p data-testid={`host-invite-guest-name-${invite.invite_id}`} style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.04 }}>
            {invite.guest_name || 'Guest'}
          </p>
          <p style={{ margin: '4px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-2)', lineHeight: 1.14 }}>
            {listingMeta.displayName}
          </p>
          {listingMeta.displayAddress ? (
            <p style={{ margin: '2px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', lineHeight: 1.14 }}>
              {listingMeta.displayAddress}
            </p>
          ) : null}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span data-testid={`host-invite-pass-pill-${invite.invite_id}`} style={{ display: 'inline-block', padding: '5px 10px', borderRadius: 9999, background: pill.bg, color: pill.color, fontFamily: "'Inria Serif', serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.01em' }}>
            {pill.label}
          </span>
          <p style={{ margin: '8px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 10, color: 'var(--text-3)' }}>
            {section === 'open' ? 'Expiring' : 'Starting'}
          </p>
          <p style={{ margin: '3px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text)', fontWeight: 700 }}>
            {formatEuropeanDate(section === 'open' ? invite.checkout_date : invite.check_in_date)}
          </p>
        </div>
      </div>

      {progress && section === 'open' ? (
        <div style={{ marginTop: 14 }} data-testid={`host-invite-progress-${invite.invite_id}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-2)', fontWeight: 700 }}>
              Day {progress.currentDay} of {progress.totalDays}
            </span>
            {venueCount > 0 ? (
              <span data-testid={`host-invite-venue-count-${invite.invite_id}`} style={{ padding: '2px 9px', borderRadius: 9999, background: 'var(--surface-2)', fontFamily: "'Inria Serif', serif", fontSize: 11, color: 'var(--text-2)', fontWeight: 700 }}>
                {venueCount} {venueCount === 1 ? 'venue' : 'venues'} visited
              </span>
            ) : null}
          </div>
          <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-2)', overflow: 'hidden' }}>
            <div data-testid={`host-invite-progress-bar-${invite.invite_id}`} style={{ width: `${progress.pct}%`, height: '100%', background: 'linear-gradient(90deg,#169C3A,#11B539)', borderRadius: 999, transition: 'width 240ms ease' }} />
          </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <motion.button
          type="button"
          onClick={() => onDeactivate(invite)}
          data-testid={`host-deactivate-invite-${invite.invite_id}`}
          aria-label="Deactivate invitation"
          whileHover={{ y: -1.5 }}
          whileTap={{ scale: 0.94 }}
          style={{ width: 44, height: 44, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(221,6,8,0.22)', background: 'var(--surface)', color: '#DD0608', cursor: 'pointer', boxShadow: 'var(--premium-shadow-nested)', flexShrink: 0 }}
        >
          <ShieldOff size={20} />
        </motion.button>
        <motion.button
          type="button"
          onClick={() => canMessage && onMessage(invite)}
          disabled={!canMessage}
          data-testid={`host-message-guest-${invite.invite_id}`}
          className="premium-pill-button"
          whileHover={canMessage ? { y: -1.5 } : undefined}
          whileTap={canMessage ? { scale: 0.985 } : undefined}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, cursor: canMessage ? 'pointer' : 'not-allowed', opacity: canMessage ? 1 : 0.55, color: '#169C3A' }}
        >
          <MessageSquare size={18} /> Message Guest
        </motion.button>
      </div>
    </motion.div>
  );
};

export default HostApprovedInviteCard;
